

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Header from './Header';

const TABS = [
  { id: 'personal',    name: 'Personal',     icon: 'person' },
  { id: 'company',     name: 'Company',      icon: 'business' },
  { id: 'team',        name: 'Team',         icon: 'group' },
  { id: 'paymentinfo', name: 'Payment Info', icon: 'payment' },
  { id: 'graphics',    name: 'Graphics',     icon: 'image' },
  { id: 'social',      name: 'Social',       icon: 'share' },
  { id: 'billing',     name: 'Billing',      icon: 'payments' },
  { id: 'shipping',    name: 'Shipping',     icon: 'local_shipping' },
  { id: 'settings',   name: 'Settings',     icon: 'settings' },
];


export default function AccountSettings() {
  const appStorage = window.app?.storage || localStorage;
  const token = appStorage.getItem('token') || localStorage.getItem('token') || sessionStorage.getItem('token');
  const isAuthenticated = !!token;
  const appname = window.app?.appName;
  const environmentVariable = window.app?.appEnvName;
  const submissionLambdaUrl = window.site?.submissionApiBaseUrl;
  const commonApi = (window.site?.config || {}).commonApi;

  const [activeTab, setActiveTab] = useState('personal');

  // ─── Shared ───────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [addressRegions, setAddressRegions] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [documentId, setDocumentId] = useState(null);

  // New state for creator creation flow
  const [isMetadataSaved, setIsMetadataSaved] = useState(false);
  const [createdUserId, setCreatedUserId] = useState(null);
  const [metadataData, setMetadataData] = useState({});

  const authUserId = appStorage.getItem('user_id') || localStorage.getItem('userid') || sessionStorage.getItem('userid');

  // ─── Personal ─────────────────────────────────────────────────────────────────
  const [userDetails, setUserDetails] = useState({
    firstname: '', lastname: '', companyName: '', email: '',
    address: '', city: '', country: '', state: '', postalCode: '',
    homePhoneDialingCode: '', homePhone: '',
    mobileDialingCode: '', mobileNumber: '',
    dateOfBirth: '', ethnicity: '', gender: '',
    creatorType: '', jobType: '', status: 'active', description: '',
  });
  const dateInputRef = useRef(null);

  // ─── Company ──────────────────────────────────────────────────────────────────
  const [companyData, setCompanyData] = useState({
    address: '', city: '', state: '', country: '', postalCode: '',
    officePhoneDialingCode: '', officePhone: '',
    mobileDialingCode: '', mobileNumber: '', email: ''
  });
  const [companyRegions, setCompanyRegions] = useState([]);
  const [isSavingCompany, setIsSavingCompany] = useState(false);

  // ─── Team ─────────────────────────────────────────────────────────────────────
  const [teamData, setTeamData] = useState({
    talentAgentName: '', talentAgentAddress: '', talentAgentCity: '', talentAgentState: '',
    talentAgentCountry: '', talentAgentPostalCode: '', talentAgentPhoneDialingCode: '',
    talentAgentPhoneNumber: '', talentAgentMobileDialingCode: '', talentAgentMobileNumber: '', talentAgentEmail: '',
    talentManagerName: '', talentManagerAddress: '', talentManagerCity: '', talentManagerState: '',
    talentManagerCountry: '', talentManagerPostalCode: '', talentManagerPhoneDialingCode: '',
    talentManagerPhoneNumber: '', talentManagerMobileDialingCode: '', talentManagerMobileNumber: '', talentManagerEmail: '',
    publicistName: '', publicistAddress: '', publicistCity: '', publicistState: '',
    publicistCountry: '', publicistPostalCode: '', publicistPhoneDialingCode: '',
    publicistPhoneNumber: '', publicistMobileDialingCode: '', publicistMobileNumber: '', publicistEmail: '',
    legalRepName: '', legalRepAddress: '', legalRepCity: '', legalRepState: '',
    legalRepCountry: '', legalRepPostalCode: '', legalRepPhoneDialingCode: '',
    legalRepPhoneNumber: '', legalRepMobileDialingCode: '', legalRepMobileNumber: '', legalRepEmail: '',
  });
  const [teamRegions, setTeamRegions] = useState([]);
  const [isSavingTeam, setIsSavingTeam] = useState(false);

  // ─── Payment Info ─────────────────────────────────────────────────────────────
  const [paymentData, setPaymentData] = useState({
    quickbooksVendorId: '', dunsNumber: '', federalTaxPayerId: '',
    awsAccountId: '', bankAccountNumber: '', bankRoutingNumber: '',
  });
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  // ─── Graphics ─────────────────────────────────────────────────────────────────
  const [uploads, setUploads] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, key: null, label: null, field: null });

  const graphicsSpecs = [
    { key: 'Landscape_1920x1080', class: 'landscape-1920x1080', width: 1920, height: 1080, label: 'Landscape Image', type: 'JPG/PNG', description: 'landscape image in .jpg or .png format', field: 'landscapeImage', ratio: 1920 / 1080 },
    { key: 'Portrait_1000x1294',  class: 'portrait-1000x1294',  width: 1000, height: 1294, label: 'Portrait Image',   type: 'JPG/PNG', description: 'portrait image in .jpg or .png format',  field: 'portraitImage',  ratio: 1000 / 1294 },
    { key: 'Logo',                class: 'logo',                 width: null, height: null, label: 'Logo',             type: 'PNG',     description: 'logo in .png format',                   field: 'logo',           ratio: 1 },
  ];

  // ─── Social ───────────────────────────────────────────────────────────────────
  const [socialData, setSocialData] = useState({
    instagram: '', facebook: '', twitter: '', youtube: '',
    soundcloud: '', spotify: '', appleItunes: '', website: '',
    imdb: '', filmfreeway: '', stage32: '', linkedin: '',
  });
  const [isSavingSocial, setIsSavingSocial] = useState(false);

  // ─── Address Cards (Billing / Shipping tabs) ──────────────────────────────────
  const [addresses, setAddresses] = useState([]);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ name: '', address: '', country: '', state: '', city: '', zipcode: '', isDefault: false });
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [addrCardDeleteModal, setAddrCardDeleteModal] = useState({ isOpen: false, addressId: null, addressName: '' });

  const [creatorTypes, setCreatorTypes] = useState([]);

  // ─── Settings / User Preferences ─────────────────────────────────────────────
  const [userPreferences, setUserPreferences] = useState({
    mediaCovert: false,
    dolbyOptiview: false,
    googleTranscoder: false,
    hiScale: false,
    atemeTitan: false,
    dolbyHybrik: false,
    accurateSubtitle: false,
    closedCaptionCreator: false,
  });
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const showToast = (message, type = 'error') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const getCountries = () => {
    axios.post(commonApi + '/getcountries?&appname=' + (appname || 'envoidemo'))
      .then(r => { if (r?.data?.result) setCountries(r.data.result); })
      .catch(err => console.error('getcountries error', err));
  };

  const fetchRegions = (alpha3, setter) => {
    axios.post(commonApi + '/getregions?&alpha3=' + alpha3)
      .then(r => { if (r?.data?.result) setter(r.data.result); })
      .catch(err => console.error('getregions error', err));
  };

  const getDialingCodeOptions = () =>
    countries.map(c => ({ value: c.idc || `+${c.phone_code}`, label: `${c.countryname} (${c.idc || '+' + c.phone_code})` }));

  // ─── Fetch lookups (ethnicity, gender, creator/job types) ────────────────────
  const fetchLookupsAndConfig = () => {
    axios.get(`${commonApi}/lookups/creatortype,team,school/envoidemo`)
      .then(r => {
        if (r?.data?.result && Array.isArray(r.data.result)) {
          const ct = r.data.result.find(i => i.type === 'creatortype');
          if (ct) {
            setCreatorTypes(ct.values || []);
          }
        }
      })
      .catch(err => console.error('lookups error', err));
  };

  // ─── Load saved metadata from localStorage ────────────────────────────────────
  useEffect(() => {
    const savedMetadata = localStorage.getItem('creator_metadata_saved');

    const savedUserId = localStorage.getItem('uid') || JSON.parse(sessionStorage.getItem('user')) || null;

    console.log('Loaded saved metadata flag:', savedMetadata);
    console.log('Loaded saved user ID:', savedUserId);

    if (savedMetadata === 'true') {
      setIsMetadataSaved(true);
    }

    if (savedUserId) {
      setCreatedUserId(savedUserId);
      setMetadataData(prev => ({ ...prev, _id: savedUserId }));
    }
  }, []);

  // ─── Fetch All User Data ───────────────────────────────────────────────────────
  useEffect(() => {
    getCountries();
    fetchLookupsAndConfig();

    const userId = createdUserId || authUserId;
    if (token && userId) {
      fetchAllUserData(userId);
    }

  }, [token, createdUserId]);

  useEffect(() => {
    if (token && appname) fetchAddresses();
  }, [token, appname]);

  const fetchAddresses = async () => {
    try {
      const lambdaUrl = window.site.screeningApiBaseUrl;
      const resp = await axios.post(
        `${lambdaUrl}/manageAddress?appname=${appname || 'envoidemo'}&env=${environmentVariable || 'dev'}&token=${token}`,
        { activity: 'fetch' },
        { headers: { 'accept': 'application/json, text/plain, */*', 'content-type': 'application/json' } }
      );
      const record = resp.data?.result?.[0];
      if (!record) return;

      const mapItems = (items, type) =>
        (items || []).map(item => ({
          id: item.id || item._id || crypto.randomUUID?.() || Date.now().toString(),
          type,
          name: item.name || '',
          address: item.address || '',
          country: item.country && item.countryCode ? `${item.country}/${item.countryCode}` : (item.country || ''),
          state: item.region && item.regionCode ? `${item.region}/${item.regionCode}` : (item.region || ''),
          city: item.city || '',
          zipcode: item.postalcode || item.postalCode || '',
          isDefault: !!item.default,
        }));

      setAddresses([
        ...mapItems(record.billingAddress, 'Billing'),
        ...mapItems(record.shippingAddress, 'Shipping'),
      ]);
    } catch (err) {
      console.warn('Failed to fetch addresses:', err);
    }
  };

  const fetchAllUserData = async (userId) => {
    try {
      setIsLoading(true);
      const resp = await axios.post(
        `${submissionLambdaUrl}/user?appname=${appname || 'envoidemo'}&token=${token}&env=${environmentVariable || 'dev'}&id=${userId}`,
        {},
        { headers: { 'accept': 'application/json, text/plain, */*', 'content-length': '0' } }
      );
      if (resp.data?.result?.data?.[0]) {
        const u = resp.data.result.data[0];
        // Store the MongoDB _id for all subsequent manageUser calls
        setDocumentId(u._id || userId);

        // Personal
        setUserDetails({
          firstname: u.firstname || '',
          lastname: u.lastname || '',
          companyName: u.companyAddress?.name || '',
          email: u.emailid || u.email || '',
          address: u.companyAddress?.address || u.address || '',
          city: u.companyAddress?.city || u.city || '',
          country: u.companyAddress?.country || u.country || '',
          state: u.companyAddress?.state || u.state || '',
          postalCode: u.companyAddress?.postalCode || u.zipcode || '',
          homePhoneDialingCode: u.homePhone?.dialingCode || '',
          homePhone: u.homePhone?.number || '',
          mobileDialingCode: u.mobileNumber?.dialingCode || '',
          mobileNumber: u.mobileNumber?.number || '',
          dateOfBirth: u.dateOfBirth || '',
          ethnicity: u.ethnicity || '',
          gender: u.gender || '',
          creatorType: u.creatorType || '',
          jobType: u.jobType || '',
          status: u.status || 'active',
          description: u.description || '',
        });
        if (u.companyAddress?.country || u.country) fetchRegions(u.companyAddress?.country || u.country, setRegions);

        // Company
        if (u.companyAddress) {
          setCompanyData({
            address: u.companyAddress.address || '',
            city: u.companyAddress.city || '',
            state: u.companyAddress.state || '',
            country: u.companyAddress.country || '',
            postalCode: u.companyAddress.postalCode || '',
            officePhoneDialingCode: u.officePhone?.dialingCode || '',
            officePhone: u.officePhone?.number || '',
            mobileDialingCode: u.mobileNumber?.dialingCode || '',
            mobileNumber: u.mobileNumber?.number || '',
            email: u.emailid || u.email || '',
          });
          if (u.companyAddress.country) fetchRegions(u.companyAddress.country, setCompanyRegions);
        }

        // Team
        if (u.team) {
          const t = u.team;
          setTeamData({
            talentAgentName: t.talentAgent?.name || '', talentAgentAddress: t.talentAgent?.address || '',
            talentAgentCity: t.talentAgent?.city || '', talentAgentState: t.talentAgent?.state || '',
            talentAgentCountry: t.talentAgent?.country || '', talentAgentPostalCode: t.talentAgent?.postalCode || '',
            talentAgentPhoneDialingCode: t.talentAgent?.phoneDialingCode || '', talentAgentPhoneNumber: t.talentAgent?.phoneNumber || '',
            talentAgentMobileDialingCode: t.talentAgent?.mobileDialingCode || '', talentAgentMobileNumber: t.talentAgent?.mobileNumber || '',
            talentAgentEmail: t.talentAgent?.email || '',
            talentManagerName: t.talentManager?.name || '', talentManagerAddress: t.talentManager?.address || '',
            talentManagerCity: t.talentManager?.city || '', talentManagerState: t.talentManager?.state || '',
            talentManagerCountry: t.talentManager?.country || '', talentManagerPostalCode: t.talentManager?.postalCode || '',
            talentManagerPhoneDialingCode: t.talentManager?.phoneDialingCode || '', talentManagerPhoneNumber: t.talentManager?.phoneNumber || '',
            talentManagerMobileDialingCode: t.talentManager?.mobileDialingCode || '', talentManagerMobileNumber: t.talentManager?.mobileNumber || '',
            talentManagerEmail: t.talentManager?.email || '',
            publicistName: t.publicist?.name || '', publicistAddress: t.publicist?.address || '',
            publicistCity: t.publicist?.city || '', publicistState: t.publicist?.state || '',
            publicistCountry: t.publicist?.country || '', publicistPostalCode: t.publicist?.postalCode || '',
            publicistPhoneDialingCode: t.publicist?.phoneDialingCode || '', publicistPhoneNumber: t.publicist?.phoneNumber || '',
            publicistMobileDialingCode: t.publicist?.mobileDialingCode || '', publicistMobileNumber: t.publicist?.mobileNumber || '',
            publicistEmail: t.publicist?.email || '',
            legalRepName: t.legalRep?.name || '', legalRepAddress: t.legalRep?.address || '',
            legalRepCity: t.legalRep?.city || '', legalRepState: t.legalRep?.state || '',
            legalRepCountry: t.legalRep?.country || '', legalRepPostalCode: t.legalRep?.postalCode || '',
            legalRepPhoneDialingCode: t.legalRep?.phoneDialingCode || '', legalRepPhoneNumber: t.legalRep?.phoneNumber || '',
            legalRepMobileDialingCode: t.legalRep?.mobileDialingCode || '', legalRepMobileNumber: t.legalRep?.mobileNumber || '',
            legalRepEmail: t.legalRep?.email || '',
          });
        }

        // Addresses
        if (u.addresses) {
          const sab = u.addresses.sameAsBilling || false;
          if (u.addresses.billing?.country) fetchRegions(u.addresses.billing.country, () => {});
          if (!sab && u.addresses.shipping?.country) fetchRegions(u.addresses.shipping.country, () => {});
        }

        // Payment Info
        if (u.paymentInfo) {
          setPaymentData({
            quickbooksVendorId: u.paymentInfo.quickbooksVendorId || '',
            dunsNumber: u.paymentInfo.dunsNumber || '',
            federalTaxPayerId: u.paymentInfo.federalTaxPayerId || '',
            awsAccountId: u.paymentInfo.awsAccountId || '',
            bankAccountNumber: u.paymentInfo.bankAccountNumber || '',
            bankRoutingNumber: u.paymentInfo.bankRoutingNumber || '',
          });
        }

        // Graphics
        const existingUploads = {};
        if (u.landscapeImage) existingUploads['Landscape_1920x1080'] = { url: u.landscapeImage, label: 'Landscape Image', file: { name: 'Existing' } };
        if (u.portraitImage)  existingUploads['Portrait_1000x1294']  = { url: u.portraitImage,  label: 'Portrait Image',   file: { name: 'Existing' } };
        if (u.logo)           existingUploads['Logo']                = { url: u.logo,           label: 'Logo',             file: { name: 'Existing' } };
        if (Object.keys(existingUploads).length) setUploads(existingUploads);

        // Social
        if (u.socialLinks) {
          setSocialData({
            instagram: u.socialLinks.instagram || '', facebook: u.socialLinks.facebook || '',
            twitter: u.socialLinks.twitter || '', youtube: u.socialLinks.youtube || '',
            soundcloud: u.socialLinks.soundcloud || '', spotify: u.socialLinks.spotify || '',
            appleItunes: u.socialLinks.appleItunes || '', website: u.socialLinks.website || '',
            imdb: u.socialLinks.imdb || '', filmfreeway: u.socialLinks.filmfreeway || '',
            stage32: u.socialLinks.stage32 || '', linkedin: u.socialLinks.linkedin || '',
          });
        }
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Handler for user creation from creator flow ─────────────────────────────
  const handleUserCreated = (userId, userData) => {
    setCreatedUserId(userId);
    setMetadataData(prev => ({
      ...prev,
      ...userData,
      _id: userId
    }));
    setIsMetadataSaved(true);
    localStorage.setItem('creator_metadata_saved', 'true');
    localStorage.setItem('creator_user_id', userId);

    // Automatically switch to company tab after successful creation
    setActiveTab('company');
    showToast('User created successfully! Now you can add company information.', 'success');
  };

  // ─── Helper function to get the current user ID for API calls ─────────────────
  const getCurrentUserId = () => {
    return documentId || createdUserId || authUserId || metadataData._id || appStorage.getItem('user_id');
  };

  const callManageUser = async (payload) => {
    const _id = getCurrentUserId();
    if (!_id) {
      throw new Error('No user ID available');
    }

    return axios.post(
      `${submissionLambdaUrl}/manageUser?appname=${appname || 'envoidemo'}&env=${environmentVariable || 'dev'}&token=${token}&_id=${_id}`,
      payload,
      { headers: { 'accept': 'application/json, text/plain, */*', 'content-type': 'application/json' } }
    );
  };

  // ─── Fetch updated user data after save ───────────────────────────────────────
  const fetchUpdatedUser = async (_id) => {
    try {
      const response = await axios.post(
        `${submissionLambdaUrl}/user?appname=${appname}&token=${token}&env=${environmentVariable || 'dev'}&id=${_id}`,
        {},
        {
          headers: {
            'accept': 'application/json, text/plain, */*',
            'content-length': '0'
          }
        }
      );
      if (response?.data?.result?.data?.[0]) {
        return response.data.result.data[0];
      }
      return null;
    } catch (error) {
      console.error('Error fetching updated user:', error);
      throw error;
    }
  };

  // ─── Personal Handlers ────────────────────────────────────────────────────────
  const handlePersonalChange = (field, value) => {
    if (field === 'country') {
      setUserDetails(prev => ({ ...prev, country: value, state: '' }));
      setRegions([]);
      if (value) fetchRegions(value, setRegions);
    } else {
      setUserDetails(prev => ({ ...prev, [field]: value }));
    }
  };

  const updateUserDetails = async () => {
    if (!userDetails.firstname?.trim()) { showToast('First Name is required.'); return; }
    if (!userDetails.lastname?.trim()) { showToast('Last Name is required.'); return; }
    try {
      setIsLoading(true);
      await callManageUser({
        firstname: userDetails.firstname,
        lastname: userDetails.lastname,
        emailid: userDetails.email,
        companyAddress: {
          name: userDetails.companyName,
          address: userDetails.address,
          city: userDetails.city,
          country: userDetails.country,
          state: userDetails.state,
          postalCode: userDetails.postalCode,
        },
        homePhone: userDetails.homePhone ? { dialingCode: userDetails.homePhoneDialingCode, number: userDetails.homePhone } : undefined,
        mobileNumber: userDetails.mobileNumber ? { dialingCode: userDetails.mobileDialingCode, number: userDetails.mobileNumber } : undefined,
        dateOfBirth: userDetails.dateOfBirth,
        ethnicity: userDetails.ethnicity,
        gender: userDetails.gender,
        creatorType: userDetails.creatorType,
        jobType: userDetails.jobType,
        status: userDetails.status,
        description: userDetails.description,
      });

      // Fetch updated data
      const updatedUser = await fetchUpdatedUser(getCurrentUserId());
      if (updatedUser) {
        // Update local state with fresh data
        // You might want to refresh all data here
      }

      showToast('Personal info saved successfully.', 'success');
    } catch (err) {
      console.error('Error updating user details:', err);
      showToast('Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Company Handlers ─────────────────────────────────────────────────────────
  const handleCompanyChange = (field, value) => {
    if (field === 'country') {
      setCompanyData(prev => ({ ...prev, country: value, state: '' }));
      setCompanyRegions([]);
      if (value) fetchRegions(value, setCompanyRegions);
    } else {
      setCompanyData(prev => ({ ...prev, [field]: value }));
    }
  };

  const saveCompany = async () => {
    try {
      setIsSavingCompany(true);
      await callManageUser({
        companyAddress: {
          address: companyData.address, city: companyData.city,
          state: companyData.state, country: companyData.country,
          postalCode: companyData.postalCode,
        },
        officePhone: companyData.officePhone ? { dialingCode: companyData.officePhoneDialingCode, number: companyData.officePhone } : undefined,
        mobileNumber: companyData.mobileNumber ? { dialingCode: companyData.mobileDialingCode, number: companyData.mobileNumber } : undefined,
        emailid: companyData.email,
      });
      showToast('Company info saved.', 'success');
    } catch (err) {
      showToast('Failed to save company info.');
    } finally {
      setIsSavingCompany(false);
    }
  };

  // ─── Team Handlers ────────────────────────────────────────────────────────────
  const handleTeamChange = (field, value) => {
    if (field.includes('Country')) {
      const stateField = field.replace('Country', 'State');
      setTeamData(prev => ({ ...prev, [field]: value, [stateField]: '' }));
      if (value) {
        // Extract alpha3 code from country value if needed
        const countryCode = value.includes('/') ? value.split('/')[1] : value;
        fetchRegions(countryCode, setTeamRegions);
      } else {
        setTeamRegions([]);
      }
    } else {
      setTeamData(prev => ({ ...prev, [field]: value }));
    }
  };

  const saveTeam = async () => {
    try {
      setIsSavingTeam(true);
      await callManageUser({
        team: {
          talentAgent: {
            name: teamData.talentAgentName, address: teamData.talentAgentAddress,
            city: teamData.talentAgentCity, state: teamData.talentAgentState,
            country: teamData.talentAgentCountry, postalCode: teamData.talentAgentPostalCode,
            phoneDialingCode: teamData.talentAgentPhoneDialingCode, phoneNumber: teamData.talentAgentPhoneNumber,
            mobileDialingCode: teamData.talentAgentMobileDialingCode, mobileNumber: teamData.talentAgentMobileNumber,
            email: teamData.talentAgentEmail,
          },
          talentManager: {
            name: teamData.talentManagerName, address: teamData.talentManagerAddress,
            city: teamData.talentManagerCity, state: teamData.talentManagerState,
            country: teamData.talentManagerCountry, postalCode: teamData.talentManagerPostalCode,
            phoneDialingCode: teamData.talentManagerPhoneDialingCode, phoneNumber: teamData.talentManagerPhoneNumber,
            mobileDialingCode: teamData.talentManagerMobileDialingCode, mobileNumber: teamData.talentManagerMobileNumber,
            email: teamData.talentManagerEmail,
          },
          publicist: {
            name: teamData.publicistName, address: teamData.publicistAddress,
            city: teamData.publicistCity, state: teamData.publicistState,
            country: teamData.publicistCountry, postalCode: teamData.publicistPostalCode,
            phoneDialingCode: teamData.publicistPhoneDialingCode, phoneNumber: teamData.publicistPhoneNumber,
            mobileDialingCode: teamData.publicistMobileDialingCode, mobileNumber: teamData.publicistMobileNumber,
            email: teamData.publicistEmail,
          },
          legalRep: {
            name: teamData.legalRepName, address: teamData.legalRepAddress,
            city: teamData.legalRepCity, state: teamData.legalRepState,
            country: teamData.legalRepCountry, postalCode: teamData.legalRepPostalCode,
            phoneDialingCode: teamData.legalRepPhoneDialingCode, phoneNumber: teamData.legalRepPhoneNumber,
            mobileDialingCode: teamData.legalRepMobileDialingCode, mobileNumber: teamData.legalRepMobileNumber,
            email: teamData.legalRepEmail,
          },
        }
      });
      showToast('Team info saved.', 'success');
    } catch (err) {
      showToast('Failed to save team info.');
    } finally {
      setIsSavingTeam(false);
    }
  };


  // ─── Payment Info Handlers ────────────────────────────────────────────────────
  const savePayment = async () => {
    try {
      setIsSavingPayment(true);
      await callManageUser({ paymentInfo: paymentData });
      showToast('Payment info saved.', 'success');
    } catch (err) {
      showToast('Failed to save payment info.');
    } finally {
      setIsSavingPayment(false);
    }
  };

  // ─── Graphics Handlers ────────────────────────────────────────────────────────
  const handleFileUpload = async (key, label, field, file) => {
    if (!file) return;
    setUploadProgress(prev => ({ ...prev, [key]: 5 }));
    try {
      const cleanFileName = file.name.replace(/\s+/g, '');
      const ext = cleanFileName.split('.').pop();
      const base = cleanFileName.replace(`.${ext}`, '');
      const finalFileName = `${base}${Date.now()}.${ext}`;
      const sourcePath = `EnvoiDemo${appname}/account/${finalFileName}`;

      setUploadProgress(prev => ({ ...prev, [key]: 10 }));

      const urlResp = await fetch(
        `${submissionLambdaUrl}/uploadURL?appname=${appname || 'envoidemo'}&env=${environmentVariable || 'dev'}&token=${token}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_bucket: 'envoi-kj-resources', sourcepath: sourcePath }) }
      );
      const urlData = await urlResp.json();
      const signedUrl = urlData?.result;
      if (!signedUrl) throw new Error('No signed URL');

      setUploadProgress(prev => ({ ...prev, [key]: 20 }));

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) setUploadProgress(prev => ({ ...prev, [key]: Math.round(20 + e.loaded / e.total * 60) }));
        });
        xhr.addEventListener('load', () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      setUploadProgress(prev => ({ ...prev, [key]: 85 }));
      const finalUrl = `https://envoi-common-resources.imgix.net/${sourcePath}`;
      await callManageUser({ [field]: finalUrl });

      setUploads(prev => ({ ...prev, [key]: { url: finalUrl, label, file } }));
      setUploadProgress(prev => ({ ...prev, [key]: 100 }));
      showToast(`${label} uploaded successfully.`, 'success');
      setTimeout(() => setUploadProgress(prev => ({ ...prev, [key]: null })), 1200);
    } catch (err) {
      console.error('Upload error:', err);
      showToast(`Failed to upload ${label}.`);
      setUploadProgress(prev => ({ ...prev, [key]: null }));
    }
  };

  const openDeleteModal = (key, label, field, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, key, label, field });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, key: null, label: null, field: null });

  const confirmDeleteGraphic = async () => {
    const { key, field, label } = deleteModal;
    try {
      await callManageUser({ [field]: null });
      setUploads(prev => { const n = { ...prev }; delete n[key]; return n; });
      showToast(`${label} removed.`, 'success');
    } catch (err) {
      showToast(`Failed to remove ${label}.`);
    } finally {
      closeDeleteModal();
    }
  };

  // ─── Social Handlers ──────────────────────────────────────────────────────────
  const saveSocial = async () => {
    try {
      setIsSavingSocial(true);
      const links = {};
      Object.entries(socialData).forEach(([k, v]) => { if (v) links[k] = v; });
      await callManageUser({ socialLinks: links });
      showToast('Social links saved.', 'success');
    } catch (err) {
      showToast('Failed to save social links.');
    } finally {
      setIsSavingSocial(false);
    }
  };

  // ─── Address Cards Handlers (Billing/Shipping tabs) ───────────────────────────
  // addresses are persisted via API; localStorage is no longer the source of truth

  const openAddAddress = () => {
    setEditingAddressId(null);
    setAddressForm({ name: '', address: '', country: '', state: '', city: '', zipcode: '', isDefault: false });
    setAddressRegions([]);
    setIsAddingAddress(true);
  };

  const openEditAddress = (item) => {
    setEditingAddressId(item.id);
    setAddressForm({ name: item.name || '', address: item.address || '', country: item.country || '', state: item.state || '', city: item.city || '', zipcode: item.zipcode || '', isDefault: !!item.isDefault });
    if (item.country) {
      const a3 = item.country.split('/')[1];
      if (a3) fetchRegions(a3, setAddressRegions);
    }
    setIsAddingAddress(true);
  };

  const handleAddressFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      const a3 = value.split('/')[1];
      if (a3) fetchRegions(a3, setAddressRegions);
      setAddressForm(prev => ({ ...prev, country: value, state: '' }));
    } else {
      setAddressForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveAddressCard = async (addressType) => {
    const { name, address, country, state, city, zipcode, isDefault } = addressForm;
    if (!name || !address || !country || !state || !city || !zipcode) { showToast('Please fill in all fields.'); return; }
    try {
      setIsAddressLoading(true);

      const [countryName, countryCode] = country.split('/');
      const [regionName, regionCode] = state.split('/');
      const addressId = editingAddressId || (crypto.randomUUID?.() ?? Date.now().toString());
      const lambdaUrl = window.site.screeningApiBaseUrl;
      const addressPayload = {
        type: addressType,
        countryCode: countryCode || '',
        country: countryName || '',
        address,
        region: regionName || '',
        regionCode: regionCode || '',
        city,
        postalcode: zipcode,
        id: addressId,
        activity: 'manage',
        default: !!isDefault,
      };

      await axios.post(
        `${lambdaUrl}/manageAddress?appname=${appname || 'envoidemo'}&token=${token}&addressType=${addressType}&env=${environmentVariable || 'dev'}`,
        addressPayload,
        { headers: { 'accept': 'application/json, text/plain, */*', 'content-type': 'application/json' } }
      );

      const entry = { id: addressId, type: addressType, ...addressForm, isDefault: !!isDefault };
      setAddresses(prev => editingAddressId ? prev.map(a => a.id === editingAddressId ? entry : a) : [...prev, entry]);
      showToast(`${addressType} address saved.`, 'success');
      setTimeout(() => { setIsAddingAddress(false); setEditingAddressId(null); }, 1200);
    } catch (err) {
      showToast('Failed to save address.');
    } finally {
      setIsAddressLoading(false);
    }
  };

  const openAddrCardDeleteModal = (id, name) => setAddrCardDeleteModal({ isOpen: true, addressId: id, addressName: name });
  const closeAddrCardDeleteModal = () => setAddrCardDeleteModal({ isOpen: false, addressId: null, addressName: '' });
  const confirmAddrCardDelete = () => { setAddresses(prev => prev.filter(a => a.id !== addrCardDeleteModal.addressId)); closeAddrCardDeleteModal(); };

  // ─── Reusable Dialing Select ──────────────────────────────────────────────────
  const DialingSelect = ({ codeVal, codeOnChange, numVal, numOnChange, disabled = false, numPlaceholder = '' }) => (
    <div style={{ display: 'flex', gap: '8px' }}>
      <div style={{ ...S.field, width: '130px', flexShrink: 0, marginBottom: 0 }}>
        <select value={codeVal} onChange={codeOnChange} disabled={disabled} style={S.select}>
          <option value="">Code</option>
          {getDialingCodeOptions().map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ ...S.field, flex: 1, marginBottom: 0 }}>
        <input type="tel" value={numVal} onChange={numOnChange} placeholder={numPlaceholder} disabled={disabled} style={S.input} />
      </div>
    </div>
  );

  // ─── Styles Object ────────────────────────────────────────────────────────────
  const S = {
    sectionTitle: { color: 'var(--sectxtColor)', fontSize: '16px', fontWeight: 600, margin: '0px 0px 20px 0px' },
    label:        { display: 'block', color: '#6f7685', fontSize: '14px', marginBottom: '5px', fontWeight: 500 },
    input:        { width: '100%', background: 'transparent', border: 'none', outline: 'none', color: '#c2d0ec', fontSize: '14px', padding: 0, height: 'auto' },
    select:       { width: '100%', background: '#1e2330', border: 'none', outline: 'none', color: '#ffffff', fontSize: '14px', padding: 0, height: 'auto', appearance: 'none', WebkitAppearance: 'none' },
    field:        { border: '1px solid #39404e', borderRadius: '4px', padding: '10px 12px', marginBottom: '16px' },
    grid3:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '16px' },
    grid2:        { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' },
    btnPrimary:   { background: '#2B44FF', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' },
    btnOutline:   { background: 'transparent', border: '1px solid var(--borderColor)', color: 'var(--sectxtColor)', borderRadius: '4px', padding: '8px 20px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' },
    saveRow:      { display: 'flex', justifyContent: 'flex-end', paddingTop: '12px' },
    spinner:      { display: 'inline-block', width: '14px', height: '14px', border: '2px solid rgba(255,255,255,.3)', borderRadius: '50%', borderTopColor: '#fff', animation: 'spin 1s ease-in-out infinite' },
  };

  // ─── Access Guard ─────────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div style={{ backgroundColor: 'var(--bgColor)', minHeight: '100vh', paddingTop: '53px' }}>
        <Header />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 53px)' }}>
          <div style={{ backgroundColor: 'var(--secbgColor)', border: '1px solid #39404e', borderRadius: 8, padding: '32px 40px', textAlign: 'center' }}>
            <h1 style={{ color: 'var(--sectxtColor)', fontSize: 22, fontWeight: 600, marginBottom: 12 }}>Access Denied</h1>
            <p style={{ color: '#6f7685' }}>Please sign in to access your account settings.</p>
          </div>
        </div>
      </div>
    );
  }

  // ─── RENDER: Personal ─────────────────────────────────────────────────────────
  const renderPersonal = () => {
    if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><div className="spinner" /></div>;
    return (
      <div style={{ maxWidth: '960px' }}>
        <h2 style={S.sectionTitle}>Personal Information</h2>

        {/* Row 1 */}
        <div style={S.grid3}>
          <div>
            <label style={S.label}>First Name <span style={{ color: '#FD2222' }}>*</span></label>
            <div style={S.field}><input style={S.input} type="text" value={userDetails.firstname} onChange={e => handlePersonalChange('firstname', e.target.value)} placeholder="Enter first name" disabled={isLoading} /></div>
          </div>
          <div>
            <label style={S.label}>Last Name <span style={{ color: '#FD2222' }}>*</span></label>
            <div style={S.field}><input style={S.input} type="text" value={userDetails.lastname} onChange={e => handlePersonalChange('lastname', e.target.value)} placeholder="Enter last name" disabled={isLoading} /></div>
          </div>
          <div>
            <label style={S.label}>Company Name</label>
            <div style={S.field}><input style={S.input} type="text" value={userDetails.companyName} onChange={e => handlePersonalChange('companyName', e.target.value)} placeholder="Enter company name" disabled={isLoading} /></div>
          </div>
        </div>

        {/* Row 2 */}
        <div style={S.grid3}>
          <div>
            <label style={S.label}>Email</label>
            <div style={S.field}><input style={S.input} type="email" value={userDetails.email} onChange={e => handlePersonalChange('email', e.target.value)} placeholder="Enter email address" disabled={isLoading} /></div>
          </div>
          <div>
            <label style={S.label}>Address</label>
            <div style={S.field}><input style={S.input} type="text" value={userDetails.address} onChange={e => handlePersonalChange('address', e.target.value)} placeholder="Street address" disabled={isLoading} /></div>
          </div>
          <div>
            <label style={S.label}>City</label>
            <div style={S.field}><input style={S.input} type="text" value={userDetails.city} onChange={e => handlePersonalChange('city', e.target.value)} placeholder="City" disabled={isLoading} /></div>
          </div>
        </div>

        {/* Row 3 */}
        <div style={S.grid3}>
          <div>
            <label style={S.label}>Country</label>
            <div style={S.field}>
              <select style={S.select} value={userDetails.country} onChange={e => handlePersonalChange('country', e.target.value)} disabled={isLoading}>
                <option value="">Select Country</option>
                {countries.map((c, i) => <option key={i} value={c.alpha3 || c.code}>{c.countryname}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={S.label}>State / Region</label>
            <div style={S.field}>
              <select style={S.select} value={userDetails.state} onChange={e => handlePersonalChange('state', e.target.value)} disabled={!userDetails.country || isLoading}>
                <option value="">Select State/Region</option>
                {regions.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={S.label}>Postal Code</label>
            <div style={S.field}><input style={S.input} type="text" value={userDetails.postalCode} onChange={e => handlePersonalChange('postalCode', e.target.value)} placeholder="Postal code" disabled={isLoading} /></div>
          </div>
        </div>

        {/* Row 4 */}
        <div style={S.grid3}>
          <div>
            <label style={S.label}>Home Phone</label>
            <DialingSelect
              codeVal={userDetails.homePhoneDialingCode} codeOnChange={e => handlePersonalChange('homePhoneDialingCode', e.target.value)}
              numVal={userDetails.homePhone} numOnChange={e => handlePersonalChange('homePhone', e.target.value)} numPlaceholder="Home phone" disabled={isLoading} />
          </div>
          <div>
            <label style={S.label}>Mobile Number</label>
            <DialingSelect
              codeVal={userDetails.mobileDialingCode} codeOnChange={e => handlePersonalChange('mobileDialingCode', e.target.value)}
              numVal={userDetails.mobileNumber} numOnChange={e => handlePersonalChange('mobileNumber', e.target.value)} numPlaceholder="Mobile number" disabled={isLoading} />
          </div>
          <div>
            <label style={S.label}>Date of Birth</label>
            <div style={{ ...S.field, position: 'relative' }}>
              <input ref={dateInputRef} style={S.input} type="date" value={userDetails.dateOfBirth} onChange={e => handlePersonalChange('dateOfBirth', e.target.value)} disabled={isLoading} />
              <span className="material-symbols-outlined" onClick={() => dateInputRef.current?.showPicker()}
                style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#9aa0c3', cursor: 'pointer', pointerEvents: isLoading ? 'none' : 'auto' }}>calendar_today</span>
            </div>
          </div>
        </div>

        {/* Row 5 — Gender, Ethnicity, Creator Type */}
        <div style={S.grid3}>
          <div>
            <label style={S.label}>Gender</label>
            <div style={S.field}>
              <select style={S.select} value={userDetails.gender} onChange={e => handlePersonalChange('gender', e.target.value)} disabled={isLoading}>
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </div>
          <div>
            <label style={S.label}>Ethnicity</label>
            <div style={S.field}>
              <input style={S.input} type="text" value={userDetails.ethnicity} onChange={e => handlePersonalChange('ethnicity', e.target.value)} placeholder="Enter ethnicity" disabled={isLoading} />
            </div>
          </div>
          <div>
            <label style={S.label}>Creator Type</label>
            <div style={S.field}>
              <select style={S.select} value={userDetails.creatorType} onChange={e => handlePersonalChange('creatorType', e.target.value)} disabled={isLoading}>
                <option value="">Select Creator Type</option>
                {creatorTypes.length > 0
                  ? creatorTypes.map((t, i) => <option key={i} value={t.value || t}>{t.label || t}</option>)
                  : ['Artist', 'Musician', 'Filmmaker', 'Photographer', 'Writer', 'Podcaster', 'Streamer', 'Other'].map(t => <option key={t} value={t.toLowerCase()}>{t}</option>)
                }
              </select>
            </div>
          </div>
        </div>

        {/* Row 6 — Job Type, Status, Description */}
        <div style={S.grid3}>
          <div>
            <label style={S.label}>Job Type</label>
            <div style={S.field}>
              <input style={S.input} type="text" value={userDetails.jobType} onChange={e => handlePersonalChange('jobType', e.target.value)} placeholder="Job type" disabled={isLoading} />
            </div>
          </div>
          <div>
            <label style={S.label}>Status</label>
            <div style={S.field}>
              <select style={S.select} value={userDetails.status} onChange={e => handlePersonalChange('status', e.target.value)} disabled={isLoading}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div>
            <label style={S.label}>Description</label>
            <div style={S.field}>
              <input style={S.input} type="text" value={userDetails.description} onChange={e => handlePersonalChange('description', e.target.value)} placeholder="Short bio or description" disabled={isLoading} />
            </div>
          </div>
        </div>

        <div style={S.saveRow}>
          <button onClick={updateUserDetails} style={S.btnPrimary} disabled={isLoading}>
            {isLoading && <span style={S.spinner} />}
            {isLoading ? 'Saving...' : 'SAVE PERSONAL INFO'}
          </button>
        </div>
      </div>
    );
  };

  // ─── RENDER: Company ──────────────────────────────────────────────────────────
  const renderCompany = () => (
    <div style={{ maxWidth: '960px' }}>
      <h2 style={S.sectionTitle}>Company Information</h2>
      <div style={S.grid3}>
        <div>
          <label style={S.label}>Address</label>
          <div style={S.field}><input style={S.input} type="text" value={companyData.address} onChange={e => handleCompanyChange('address', e.target.value)} placeholder="Street address" /></div>
        </div>
        <div>
          <label style={S.label}>City</label>
          <div style={S.field}><input style={S.input} type="text" value={companyData.city} onChange={e => handleCompanyChange('city', e.target.value)} placeholder="City" /></div>
        </div>
        <div>
          <label style={S.label}>Postal Code</label>
          <div style={S.field}><input style={S.input} type="text" value={companyData.postalCode} onChange={e => handleCompanyChange('postalCode', e.target.value)} placeholder="Postal code" /></div>
        </div>
      </div>
      <div style={S.grid3}>
        <div>
          <label style={S.label}>Country</label>
          <div style={S.field}>
            <select style={S.select} value={companyData.country} onChange={e => handleCompanyChange('country', e.target.value)}>
              <option value="">Select Country</option>
              {countries.map((c, i) => <option key={i} value={c.alpha3 || c.code}>{c.countryname}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={S.label}>State / Region</label>
          <div style={S.field}>
            <select style={S.select} value={companyData.state} onChange={e => handleCompanyChange('state', e.target.value)} disabled={!companyData.country}>
              <option value="">Select State/Region</option>
              {companyRegions.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={S.label}>Email</label>
          <div style={S.field}><input style={S.input} type="email" value={companyData.email} onChange={e => handleCompanyChange('email', e.target.value)} placeholder="Company email" /></div>
        </div>
      </div>
      <div style={S.grid2}>
        <div>
          <label style={S.label}>Office Phone</label>
          <DialingSelect
            codeVal={companyData.officePhoneDialingCode} codeOnChange={e => handleCompanyChange('officePhoneDialingCode', e.target.value)}
            numVal={companyData.officePhone} numOnChange={e => handleCompanyChange('officePhone', e.target.value)} numPlaceholder="Office phone" />
        </div>
        <div>
          <label style={S.label}>Mobile Number</label>
          <DialingSelect
            codeVal={companyData.mobileDialingCode} codeOnChange={e => handleCompanyChange('mobileDialingCode', e.target.value)}
            numVal={companyData.mobileNumber} numOnChange={e => handleCompanyChange('mobileNumber', e.target.value)} numPlaceholder="Mobile number" />
        </div>
      </div>
      <div style={S.saveRow}>
        <button onClick={saveCompany} style={S.btnPrimary} disabled={isSavingCompany}>
          {isSavingCompany && <span style={S.spinner} />}
          {isSavingCompany ? 'Saving...' : 'SAVE COMPANY INFO'}
        </button>
      </div>
    </div>
  );

  // ─── RENDER: Team ─────────────────────────────────────────────────────────────
  const renderTeamSection = (prefix, title) => {
    const fields = { Name: `${prefix}Name`, Address: `${prefix}Address`, City: `${prefix}City`, Email: `${prefix}Email`, 'Postal Code': `${prefix}PostalCode` };
    return (
      <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #39404e' }}>
        <h3 style={{ color: 'var(--sectxtColor)', fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>{title}</h3>
        <div style={S.grid3}>
          {Object.entries(fields).map(([label, field]) => (
            <div key={field}>
              <label style={S.label}>{label}</label>
              <div style={S.field}>
                <input style={S.input} type={field.includes('Email') ? 'email' : 'text'} value={teamData[field] || ''}
                  onChange={e => handleTeamChange(field, e.target.value)} placeholder={label} />
              </div>
            </div>
          ))}
          <div>
            <label style={S.label}>Country</label>
            <div style={S.field}>
              <select style={S.select} value={teamData[`${prefix}Country`] || ''} onChange={e => handleTeamChange(`${prefix}Country`, e.target.value)}>
                <option value="">Select Country</option>
                {countries.map((c, i) => <option key={i} value={c.alpha3 || c.code}>{c.countryname}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={S.label}>State / Region</label>
            <div style={S.field}>
              <select style={S.select} value={teamData[`${prefix}State`] || ''} onChange={e => handleTeamChange(`${prefix}State`, e.target.value)}>
                <option value="">Select State</option>
                {teamRegions.map((r, i) => <option key={i} value={r.regionname}>{r.regionname}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={S.grid2}>
          <div>
            <label style={S.label}>Phone Number</label>
            <DialingSelect
              codeVal={teamData[`${prefix}PhoneDialingCode`] || ''} codeOnChange={e => handleTeamChange(`${prefix}PhoneDialingCode`, e.target.value)}
              numVal={teamData[`${prefix}PhoneNumber`] || ''} numOnChange={e => handleTeamChange(`${prefix}PhoneNumber`, e.target.value)} numPlaceholder="Phone" />
          </div>
          <div>
            <label style={S.label}>Mobile Number</label>
            <DialingSelect
              codeVal={teamData[`${prefix}MobileDialingCode`] || ''} codeOnChange={e => handleTeamChange(`${prefix}MobileDialingCode`, e.target.value)}
              numVal={teamData[`${prefix}MobileNumber`] || ''} numOnChange={e => handleTeamChange(`${prefix}MobileNumber`, e.target.value)} numPlaceholder="Mobile" />
          </div>
        </div>
      </div>
    );
  };

  const renderTeam = () => (
    <div style={{ maxWidth: '960px' }}>
      <h2 style={S.sectionTitle}>Team</h2>
      {renderTeamSection('talentAgent', 'Talent Agent')}
      {renderTeamSection('talentManager', 'Talent Manager')}
      {renderTeamSection('publicist', 'Publicist')}
      {renderTeamSection('legalRep', 'Legal Representative')}
      <div style={S.saveRow}>
        <button onClick={saveTeam} style={S.btnPrimary} disabled={isSavingTeam}>
          {isSavingTeam && <span style={S.spinner} />}
          {isSavingTeam ? 'Saving...' : 'SAVE TEAM INFO'}
        </button>
      </div>
    </div>
  );

  // ─── RENDER: Payment Info ─────────────────────────────────────────────────────
  const renderPaymentInfo = () => {
    const fields = [
      ['quickbooksVendorId', 'QuickBooks Vendor ID'],
      ['dunsNumber', 'DUNS Number'],
      ['federalTaxPayerId', 'Federal Tax Payer ID'],
      ['awsAccountId', 'AWS Account ID'],
      ['bankAccountNumber', 'Bank Account Number'],
      ['bankRoutingNumber', 'Bank Routing Number'],
    ];
    return (
      <div style={{ maxWidth: '960px' }}>
        <h2 style={S.sectionTitle}>Payment Information</h2>
        <div style={S.grid3}>
          {fields.map(([field, label]) => (
            <div key={field}>
              <label style={S.label}>{label}</label>
              <div style={S.field}>
                <input style={S.input} type="text" value={paymentData[field]} onChange={e => setPaymentData(prev => ({ ...prev, [field]: e.target.value }))} placeholder={label} />
              </div>
            </div>
          ))}
        </div>
        <div style={S.saveRow}>
          <button onClick={savePayment} style={S.btnPrimary} disabled={isSavingPayment}>
            {isSavingPayment && <span style={S.spinner} />}
            {isSavingPayment ? 'Saving...' : 'SAVE PAYMENT INFO'}
          </button>
        </div>
      </div>
    );
  };

  // ─── RENDER: Graphics ─────────────────────────────────────────────────────────
  const renderGraphics = () => {
    const UploadBox = ({ spec }) => {
      const { key, label, width, height, ratio, description } = spec;
      const upload = uploads[key];
      const progress = uploadProgress[key];
      const isUploading = progress !== null && progress !== undefined && progress < 100;

      const handleClick = () => {
        if (isUploading) return;
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/jpeg,image/jpg,image/png';
        input.onchange = e => {
          const file = e.target.files[0];
          if (file) handleFileUpload(key, label, spec.field, file);
        };
        input.click();
      };

      return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <label style={S.label}>{label}</label>
          <div
            style={{ position: 'relative', background: '#2a2d3e', borderRadius: '8px', border: '2px dashed #434558', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: String(ratio), minHeight: '150px', cursor: isUploading ? 'wait' : 'pointer' }}
            onClick={handleClick}
            onDrop={e => { e.preventDefault(); if (!isUploading) { const f = e.dataTransfer.files[0]; if (f) handleFileUpload(key, label, spec.field, f); } }}
            onDragOver={e => e.preventDefault()}
          >
            {upload ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <img src={upload.url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)', padding: '8px 12px' }}>
                  <span style={{ color: '#fff', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{upload.file?.name || 'Uploaded image'}</span>
                </div>
                {!isUploading && (
                  <>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                      <span style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>Click to replace</span>
                    </div>
                    <button
                      onClick={e => openDeleteModal(key, label, spec.field, e)}
                      style={{ position: 'absolute', top: '8px', right: '8px', width: '28px', height: '28px', background: '#ef4444', borderRadius: '50%', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10 }}
                      title="Delete image"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#fff' }}>delete</span>
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', padding: '16px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 34, display: 'block', margin: '0 auto 8px' }}>upload</span>
                <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', marginTop: '4px' }}>
                  {width && height ? (
                    <>
                      <div style={{ fontWeight: 500, color: '#d1d5db' }}>{width} × {height}</div>
                      <div style={{ fontSize: '10px', marginTop: '4px' }}>{description}</div>
                    </>
                  ) : (
                    <div style={{ fontWeight: 500, color: '#d1d5db' }}>Click to upload</div>
                  )}
                </div>
              </div>
            )}

            {isUploading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}>
                <div style={{ width: '80%', maxWidth: '200px', background: '#374151', borderRadius: '999px', height: '10px', marginBottom: '8px', overflow: 'hidden' }}>
                  <div style={{ background: '#3b82f6', height: '10px', borderRadius: '999px', transition: 'width 0.3s ease-out', width: `${progress}%` }} />
                </div>
                <span style={{ color: '#fff', fontSize: '12px', fontWeight: 500 }}>
                  {progress < 20 ? 'Getting ready...' : progress < 80 ? 'Uploading...' : 'Saving...'}
                </span>
                <span style={{ color: '#fff', fontSize: '12px', marginTop: '4px' }}>{progress}%</span>
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <>
        <div style={{ maxWidth: '960px', paddingBottom: '16px' }}>
          <div>
            <h2 style={S.sectionTitle}>Upload Graphics</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginTop: '16px' }}>
              {graphicsSpecs.map(spec => <UploadBox key={spec.key} spec={spec} />)}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteModal.isOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
            <div style={{ background: 'var(--secbgColor)', borderRadius: '8px', padding: '28px', width: '400px', maxWidth: '90vw', border: '1px solid #39404e' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(253,34,34,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="material-symbols-outlined" style={{ color: '#FD2222', fontSize: '26px' }}>delete</span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h2 style={{ color: 'var(--sectxtColor)', fontSize: '1rem', fontWeight: 600 }}>Delete Image</h2>
                <span className="material-symbols-outlined" onClick={closeDeleteModal} style={{ cursor: 'pointer', color: '#6f7685', fontSize: '20px' }}>close</span>
              </div>
              <p style={{ color: '#6f7685', fontSize: '14px', marginBottom: '24px' }}>
                Are you sure you want to delete this <strong style={{ color: 'var(--sectxtColor)' }}>{deleteModal.label?.toLowerCase()}</strong>?
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button onClick={closeDeleteModal} style={S.btnOutline}>Cancel</button>
                <button onClick={confirmDeleteGraphic} style={{ ...S.btnPrimary, background: '#dc2626' }}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // ─── RENDER: Social ───────────────────────────────────────────────────────────
  const renderSocial = () => {
    const platforms = [
      { key: 'instagram',   label: 'Instagram',     placeholder: 'https://instagram.com/username' },
      { key: 'facebook',    label: 'Facebook',       placeholder: 'https://facebook.com/pagename' },
      { key: 'twitter',     label: 'Twitter / X',    placeholder: 'https://twitter.com/username' },
      { key: 'youtube',     label: 'YouTube',        placeholder: 'https://youtube.com/@channel' },
      { key: 'soundcloud',  label: 'SoundCloud',     placeholder: 'https://soundcloud.com/username' },
      { key: 'spotify',     label: 'Spotify',        placeholder: 'https://open.spotify.com/artist/id' },
      { key: 'appleItunes', label: 'Apple iTunes',   placeholder: 'https://music.apple.com/artist/id' },
      { key: 'website',     label: 'Website',        placeholder: 'https://yourwebsite.com' },
      { key: 'imdb',        label: 'IMDB',           placeholder: 'https://www.imdb.com/name/nmxxxxxxx' },
      { key: 'filmfreeway', label: 'Film Freeway',   placeholder: 'https://filmfreeway.com/username' },
      { key: 'stage32',     label: 'Stage 32',       placeholder: 'https://www.stage32.com/username' },
      { key: 'linkedin',    label: 'LinkedIn',       placeholder: 'https://linkedin.com/in/username' },
    ];
    return (
      <div style={{ maxWidth: '960px' }}>
        <h2 style={S.sectionTitle}>Social Media Links</h2>
        <div style={S.grid3}>
          {platforms.map(p => (
            <div key={p.key}>
              <label style={S.label}>{p.label}</label>
              <div style={S.field}>
                <input style={S.input} type="url" value={socialData[p.key] || ''} onChange={e => setSocialData(prev => ({ ...prev, [p.key]: e.target.value }))} placeholder={p.placeholder} />
              </div>
            </div>
          ))}
        </div>
        <div style={S.saveRow}>
          <button onClick={saveSocial} style={S.btnPrimary} disabled={isSavingSocial}>
            {isSavingSocial && <span style={S.spinner} />}
            {isSavingSocial ? 'Saving...' : 'SAVE SOCIAL LINKS'}
          </button>
        </div>
      </div>
    );
  };

  // ─── RENDER: Address Cards (Billing / Shipping tabs) ──────────────────────────
  const renderAddressCards = (addressType) => {
    if (isAddressLoading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}><div className="spinner" /></div>;
    const filtered = addresses.filter(a => a.type === addressType);
    const icon = addressType === 'Billing' ? 'payment' : 'local_shipping';
    return (
      <div>
        {!isAddingAddress && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ color: 'var(--sectxtColor)', fontSize: '14px', fontWeight: 600 }}>{addressType} Address</h2>
            <button onClick={openAddAddress} style={S.btnPrimary}>Add {addressType} Address</button>
          </div>
        )}
        {isAddingAddress && (
          <div style={{ maxWidth: '960px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
              <h1 style={{ color: 'var(--sectxtColor)', fontSize: '1.2rem', fontWeight: 600 }}>{editingAddressId ? 'Edit' : 'Add'} {addressType} Address</h1>
              <button onClick={() => { setIsAddingAddress(false); setEditingAddressId(null); }} style={S.btnOutline}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_back</span> BACK
              </button>
            </div>
            <div style={S.grid3}>
              {[['name', 'Name / Label', 'e.g. Home, Office'], ['address', 'Address', 'Street Address'], ['city', 'City', 'City']].map(([n, lbl, ph]) => (
                <div key={n}>
                  <label style={S.label}>{lbl}</label>
                  <div style={S.field}>
                    <input style={S.input} type="text" name={n} value={addressForm[n]} onChange={handleAddressFormChange} placeholder={ph} />
                  </div>
                </div>
              ))}
            </div>
            <div style={S.grid3}>
              <div>
                <label style={S.label}>Country</label>
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <select name="country" value={addressForm.country} onChange={handleAddressFormChange}
                    style={{ ...S.select, paddingRight: '24px', border: '1px solid #39404e', borderRadius: '4px', padding: '10px 12px', marginBottom: '16px' }}>
                    <option value="">Select Country</option>
                    {countries.map((c, i) => <option key={i} value={`${c.countryname}/${c.alpha3}`}>{c.countryname}</option>)}
                  </select>
                  <span className="material-symbols-outlined" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-75%)', pointerEvents: 'none', fontSize: '20px', color: 'var(--primary-color)' }}>arrow_drop_down</span>
                </div>
              </div>
              <div>
                <label style={S.label}>State / Province</label>
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <select name="state" value={addressForm.state} onChange={handleAddressFormChange}
                    style={{ ...S.select, paddingRight: '24px', border: '1px solid #39404e', borderRadius: '4px', padding: '10px 12px', marginBottom: '16px' }}>
                    <option value="">Select State</option>
                    {addressRegions.map((r, i) => <option key={i} value={`${r.regionname}/${r.regioncode}`}>{r.regionname}</option>)}
                  </select>
                  <span className="material-symbols-outlined" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-75%)', pointerEvents: 'none', fontSize: '20px', color: 'var(--primary-color)' }}>arrow_drop_down</span>
                </div>
              </div>
              <div>
                <label style={S.label}>Zip / Postal Code</label>
                <div style={S.field}>
                  <input style={S.input} type="text" name="zipcode" value={addressForm.zipcode} onChange={handleAddressFormChange} placeholder="Zip Code" />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6f7685', fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!addressForm.isDefault}
                  onChange={e => setAddressForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#2B44FF' }}
                />
                Set as default {addressType} address
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => { setIsAddingAddress(false); setEditingAddressId(null); }} style={S.btnOutline} disabled={isAddressLoading}>Cancel</button>
                <button onClick={() => handleSaveAddressCard(addressType)} style={S.btnPrimary} disabled={isAddressLoading}>{isAddressLoading ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        )}
        {!isAddingAddress && filtered.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {filtered.map(item => (
              <div key={item.id} style={{ background: '#171825', borderRadius: 6, border: '1px solid var(--border-color)', overflow: 'hidden', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#2d3a55'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}>
                <div style={{ width: '100%', height: 160, background: '#1a2438', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 56, color: 'var(--accent-primary,#6366f1)', opacity: 0.75 }}>{icon}</span>
                  {item.isDefault && (
                    <span style={{
                      position: 'absolute', top: 8, right: 8,
                      background: 'rgba(16,185,129,0.15)',
                      border: '1px solid rgba(16,185,129,0.4)',
                      color: '#34d399',
                      fontSize: 10, fontWeight: 600, letterSpacing: '0.04em',
                      padding: '2px 8px', borderRadius: 20,
                      display: 'flex', alignItems: 'center', gap: 3,
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 11 }}>check_circle</span>
                      Default
                    </span>
                  )}
                </div>
                <div style={{ padding: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--foreground)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 }}>{item.name || addressType}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => openEditAddress(item)} style={{ ...S.btnOutline, padding: '4px 8px' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                      </button>
                      <button onClick={() => openAddrCardDeleteModal(item.id, item.name)} style={{ ...S.btnOutline, padding: '4px 8px' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--accent-primary,#6366f1)', flexShrink: 0, marginTop: 1 }}>location_on</span>
                    <span style={{ lineHeight: 1.4 }}>{[item.address, item.city, item.state?.split('/')[0], item.zipcode, item.country?.split('/')[0]].filter(Boolean).join(', ')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !isAddingAddress && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 0', background: '#171825', borderRadius: 8, border: '1px solid #39404e' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.3, marginBottom: '12px' }}>{icon}</span>
              <p style={{ color: '#6f7685', fontSize: '13px' }}>No {addressType.toLowerCase()} addresses yet.</p>
            </div>
          )
        )}
      </div>
    );
  };

  // ─── RENDER: Settings (User Preferences) ──────────────────────────────────────
  const renderSettings = () => {
    const boolCard = (key, label, icon, imgSrc) => {
      const value = userPreferences[key] === true;
      return (
        <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: '8px', backgroundColor: '#313345', border: '1px solid #41425A', flex: '1', minWidth: '270px', maxWidth: '340px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {imgSrc
              ? <img src={imgSrc} alt={label} style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              : icon && <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#9aa0c3' }}>{icon}</span>
            }
            <span style={{ fontSize: '14px', color: '#9aa0c3' }}>{label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: value ? '#ffffff' : '#9aa0c3' }}>{value ? 'True' : 'False'}</span>
            <button
              type="button"
              onClick={() => setUserPreferences(prev => ({ ...prev, [key]: !value }))}
              style={{ position: 'relative', display: 'inline-flex', width: '44px', height: '24px', alignItems: 'center', borderRadius: '999px', transition: 'background 0.2s', border: 'none', cursor: 'pointer', background: value ? '#2B44FF' : '#41425A' }}
            >
              <span style={{ display: 'inline-block', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transform: value ? 'translateX(22px)' : 'translateX(2px)', transition: 'transform 0.2s' }} />
            </button>
          </div>
        </div>
      );
    };

    return (
      <div style={{ maxWidth: '960px', paddingTop: '8px' }}>
        <h2 style={S.sectionTitle}>User Preferences</h2>

        {/* Media Processing */}
        <div style={{ padding: '24px', borderRadius: '12px', border: '1px solid #39404e', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span className="material-symbols-outlined" style={{ color: '#2B44FF', fontSize: '22px' }}>transform</span>
            <h3 style={{ color: 'var(--sectxtColor)', fontSize: '1rem', fontWeight: 600 }}>Media Processing</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {boolCard('mediaCovert', 'Media Covert', 'cloud', null)}
            {boolCard('dolbyOptiview', 'Dolby OptiView', null, 'https://envoi-transfer-demo.d3ky62frre6z45.amplifyapp.com/assets/logo-optiview-dark-By2YIVHz.png')}
            {boolCard('googleTranscoder', 'Google Transcoder', null, 'https://envoi-transfer-demo.d3ky62frre6z45.amplifyapp.com/assets/googlecloud-1-Dlg33M-A.svg')}
            {boolCard('hiScale', 'HiScale', null, 'https://envoi-transfer-demo.d3ky62frre6z45.amplifyapp.com/assets/hiscale-logo-B9WE_fYf.png')}
            {boolCard('atemeTitan', 'Ateme Titan', null, 'https://envoi-transfer-demo.d3ky62frre6z45.amplifyapp.com/assets/ateme-logo-BoVoPMZC.png')}
            {boolCard('dolbyHybrik', 'Dolby Hybrik', null, 'https://envoi-transfer-demo.d3ky62frre6z45.amplifyapp.com/assets/dolby_hybrik_logo-BSW3V9s7.png')}
          </div>
        </div>

        {/* Subtitle */}
        <div style={{ padding: '24px', borderRadius: '12px', border: '1px solid #39404e', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <span className="material-symbols-outlined" style={{ color: '#2B44FF', fontSize: '22px' }}>closed_caption</span>
            <h3 style={{ color: 'var(--sectxtColor)', fontSize: '1rem', fontWeight: 600 }}>Subtitle</h3>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {boolCard('accurateSubtitle', 'Accurate Subtitle', null, 'https://envoi-relay-accurate-video.d34e9vdif556t.amplifyapp.com/assets/relay-logo-CTL7fexx.svg')}
            {boolCard('closedCaptionCreator', 'Closed Caption Creator', null, 'https://envoi-relay-cc.d2rydjlrzcno0.amplifyapp.com/assets/relay-logo-CsoJhHyx.svg')}
          </div>
        </div>

        <div style={S.saveRow}>
          <button
            style={S.btnPrimary}
            disabled={isSavingPreferences}
            onClick={async () => {
              setIsSavingPreferences(true);
              try {
                // persist preferences as needed
                showToast('Preferences saved successfully.', 'success');
              } catch {
                showToast('Failed to save preferences.');
              } finally {
                setIsSavingPreferences(false);
              }
            }}
          >
            {isSavingPreferences && <span style={S.spinner} />}
            {isSavingPreferences ? 'Saving...' : 'SAVE PREFERENCES'}
          </button>
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: 'var(--bgColor)', minHeight: '100vh', display: 'flex', flexDirection: 'column', paddingTop: '53px' }}>
      <Header />

      {/* Top Tab Bar */}
      <div style={{ display: 'flex', alignItems: 'flex-end', borderBottom: '1px solid var(--borderColor)', padding: '8px 32px 0', flexShrink: 0, backgroundColor: 'var(--bgColor)', overflowX: 'auto' }}>
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button key={tab.id}
              onClick={() => { setActiveTab(tab.id); setIsAddingAddress(false); setEditingAddressId(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', fontSize: '1rem', fontWeight: 500, color: active ? 'var(--highlight)' : '#9aa0c3', background: 'transparent', border: 'none', borderBottom: active ? '2px solid var(--highlight)' : '2px solid transparent', cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s', whiteSpace: 'nowrap', fontSize: 14}}>
              <span className="material-symbols-outlined" style={{ fontSize: 20, marginRight: 2 }}>{tab.icon}</span>
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
        {activeTab === 'personal'    && renderPersonal()}
        {activeTab === 'company'     && renderCompany()}
        {activeTab === 'team'        && renderTeam()}
        {activeTab === 'paymentinfo' && renderPaymentInfo()}
        {activeTab === 'graphics'    && renderGraphics()}
        {activeTab === 'social'      && renderSocial()}
        {activeTab === 'billing'     && renderAddressCards('Billing')}
        {activeTab === 'shipping'    && renderAddressCards('Shipping')}
        {activeTab === 'settings'    && renderSettings()}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '10px', fontSize: 12, color: '#6f7685', borderTop: '1px solid var(--borderColor)', backgroundColor: 'var(--bgColor)' }}>
        © {new Date().getFullYear()} Envoi Cloud. All Rights Reserved.
      </div>

      {/* Toast */}
      <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: `translateX(-50%) translateY(${toast.show ? '0' : '200px'})`, opacity: toast.show ? 1 : 0, transition: 'all 0.3s', background: toast.type === 'error' ? '#dc2626' : '#059669', color: '#fff', padding: '12px 24px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{toast.type === 'error' ? 'error' : 'check_circle'}</span>
        <span style={{ fontWeight: 500 }}>{toast.message}</span>
      </div>

      {/* Delete Address Card Modal */}
      {addrCardDeleteModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <div style={{ background: 'var(--secbgColor)', borderRadius: '8px', padding: '28px', width: '400px', maxWidth: '90vw', border: '1px solid #39404e' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(253,34,34,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span className="material-symbols-outlined" style={{ color: '#FD2222', fontSize: '26px' }}>delete</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ color: 'var(--sectxtColor)', fontSize: '1rem', fontWeight: 600 }}>Delete Address</h2>
              <span className="material-symbols-outlined" onClick={closeAddrCardDeleteModal} style={{ cursor: 'pointer', color: '#6f7685', fontSize: '20px' }}>close</span>
            </div>
            <p style={{ color: '#6f7685', fontSize: '14px', marginBottom: '24px' }}>
              Are you sure you want to delete the <strong style={{ color: 'var(--sectxtColor)' }}>{addrCardDeleteModal.addressName}</strong> address?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={closeAddrCardDeleteModal} style={S.btnOutline}>Cancel</button>
              <button onClick={confirmAddrCardDelete} style={{ ...S.btnPrimary, background: '#dc2626' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
